import {catchError, defer, Observable, switchMap} from 'rxjs';

import {inject, Injectable, signal, WritableSignal} from '@angular/core';
import {ApiService} from '@core/api/data-access';

@Injectable({
	providedIn: 'root',
})
export class PushNotificationsService {
	private readonly VAPID_PUBLIC_KEY = 'YOUR_VAPID_PUBLIC_KEY_HERE';
	/* -------------------------------------------------------------------------------------------------------------- */
	private readonly apiService: ApiService = inject(ApiService);
	/* -------------------------------------------------------------------------------------------------------------- */
	public readonly isPushNotificationsSupported$: WritableSignal<boolean> = signal(null);
	/**
	 * The current push notifications permission.
	 * @returns {NotificationPermission} The current push notifications permission.
	 * @description ```markdown
	 * The current push notifications permission.
	 * - default: The user has not yet been asked for permission.
	 * - denied: The user has denied permission.
	 * - granted: The user has granted permission.
	 * ```
	 */
	public readonly pushNotificationsPermission$: WritableSignal<NotificationPermission> = signal(null);

	/**
	 * Indicates if push notifications are enabled.
	 * @type {WritableSignal<boolean>}
	 */
	public readonly isPushNotificationsEnabled$: WritableSignal<boolean> = signal(null);

	/**
	 * Indicates if the user is subscribed to push notifications.
	 * @type {WritableSignal<boolean>}
	 */
	public readonly isSubscribedToPush$: WritableSignal<boolean> = signal(null);

	constructor() {
		this.updateIndicators().then(() => {
			console.debug('[PushNotificationsService] Initialized');
		});
	}

	/**
	 * Updates the push notifications indicators.
	 * @returns {Promise<void>}
	 */
	public async updateIndicators(): Promise<void> {
		const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
		this.isPushNotificationsSupported$.set(isSupported);
		this.pushNotificationsPermission$.set(Notification.permission);

		if (isSupported) {
			const registration = await navigator.serviceWorker.getRegistration();
			if (registration) {
				const subscription = await registration.pushManager.getSubscription();
				this.isSubscribedToPush$.set(!!subscription);
				this.isPushNotificationsEnabled$.set(Notification.permission === 'granted');
			}
		}
	}

	/**
	 * Requests permission for push notifications.
	 * @returns {Promise<void>}
	 */
	public async requestPushNotificationsPermission(): Promise<void> {
		const permission = await Notification.requestPermission();
		this.pushNotificationsPermission$.set(permission);
		this.isPushNotificationsEnabled$.set(permission === 'granted');
	}

	public async subscribeToPushNotifications(): Promise<void> {
		const registration = await navigator.serviceWorker.getRegistration();
		if (registration) {
			const subscription = await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: this.urlBase64ToUint8Array(this.VAPID_PUBLIC_KEY),
			});
			this.isSubscribedToPush$.set(!!subscription);
		}
	}

	/**
	 * Unsubscribes the user from push notifications.
	 * @returns {Promise<void>}
	 */
	public async unsubscribeFromPushNotifications(): Promise<void> {
		const registration = await navigator.serviceWorker.getRegistration();
		if (registration) {
			const subscription = await registration.pushManager.getSubscription();
			if (subscription) {
				await subscription.unsubscribe();
				this.isSubscribedToPush$.set(false);
			}
		}
	}

	/**
	 * Sends a push notification (for testing purposes).
	 * @param {string} title - The title of the notification.
	 * @param {NotificationOptions} [options] - Additional options for the notification.
	 * @returns {Promise<void>}
	 */
	public async sendPushNotification(title: string, options?: NotificationOptions): Promise<void> {
		if (this.isPushNotificationsEnabled$()) {
			const registration = await navigator.serviceWorker.getRegistration();
			if (registration) {
				await registration.showNotification(title, options);
			}
		}
	}

	private getExistingSubscription(): Observable<PushSubscription> {
		return defer(async () => {
			const registration = await navigator.serviceWorker.getRegistration();
			if (registration) {
				return registration.pushManager.getSubscription();
			}
			return null;
		});
	}

	public sendTestNotification(message: string, scheduledTime?: Date): Observable<any> {
		return this.getExistingSubscription().pipe(
			switchMap(subscription => {
				if (!subscription) {
					throw new Error('Not subscribed to push notifications');
				}
				const payload = {
					notification: {
						title: 'Test Notification',
						body: message,
						icon: '/assets/icons/icon-128x128.png',
						badge: '/assets/icons/icon-96x96.png',
						vibrate: [100, 50, 100],
						data: {
							dateOfArrival: scheduledTime ? scheduledTime.getTime() : Date.now(),
							primaryKey: 1,
						},
					},
					subscription: subscription,
				};
				// Replace with your actual API endpoint
				// return this.http.post('/api/send-notification', payload);
				return this.apiService.post('/api/send-notification', payload);
			}),
			catchError(error => {
				console.error('Error sending test notification:', error);
				throw error;
			}),
		);
	}

	/** ------------------------------------ Private Methods ------------------------------------ */

	/**
	 * Converts a URL-safe base64 string to a Uint8Array.
	 * @param {string} base64String - The base64 string to convert.
	 * @returns {Uint8Array} The resulting Uint8Array.
	 */
	private urlBase64ToUint8Array(base64String: string): Uint8Array {
		const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
		const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
		const rawData = window.atob(base64);
		const outputArray = new Uint8Array(rawData.length);
		for (let i = 0; i < rawData.length; ++i) {
			outputArray[i] = rawData.charCodeAt(i);
		}
		return outputArray;
	}
}
